import React, { useState, useEffect } from 'react';
import * as FiIcons from 'react-icons/fi';
import ReactECharts from 'echarts-for-react';
import SafeIcon from '../../common/SafeIcon';

const { FiFileText, FiZap, FiBarChart2 } = FiIcons;

const LexicalDiversityAnalysis = ({ questions }) => {
  const [diversityScores, setDiversityScores] = useState([]);
  const [averageScore, setAverageScore] = useState(0);
  const [distribution, setDistribution] = useState({
    'Very Low': 0,
    'Low': 0,
    'Medium': 0,
    'High': 0,
    'Very High': 0
  });

  useEffect(() => {
    if (!questions.length) return;
    
    // Calculate lexical diversity for each question
    const scores = questions.map(question => {
      const score = calculateLexicalDiversity(question.text);
      
      // Categorize the score
      let category = 'Medium';
      if (score < 0.3) category = 'Very Low';
      else if (score < 0.4) category = 'Low';
      else if (score < 0.5) category = 'Medium';
      else if (score < 0.6) category = 'High';
      else category = 'Very High';
      
      return {
        id: question.id,
        text: question.text.substring(0, 80) + (question.text.length > 80 ? '...' : ''),
        score: score.toFixed(2),
        category,
        uniqueWords: countUniqueWords(question.text),
        totalWords: countWords(question.text)
      };
    });
    
    // Sort by diversity score (highest first)
    scores.sort((a, b) => b.score - a.score);
    setDiversityScores(scores);
    
    // Calculate average score
    const total = scores.reduce((sum, item) => sum + parseFloat(item.score), 0);
    setAverageScore((total / scores.length).toFixed(2));
    
    // Calculate distribution
    const dist = {
      'Very Low': 0,
      'Low': 0,
      'Medium': 0,
      'High': 0,
      'Very High': 0
    };
    
    scores.forEach(item => {
      dist[item.category] = (dist[item.category] || 0) + 1;
    });
    
    setDistribution(dist);
    
  }, [questions]);
  
  // Function to calculate lexical diversity
  const calculateLexicalDiversity = (text) => {
    if (!text || text.length === 0) return 0;
    
    // Clean and tokenize the text
    const words = text.toLowerCase()
      .replace(/[^\w\s]/g, '')
      .split(/\s+/)
      .filter(word => word.length > 0);
    
    if (words.length === 0) return 0;
    
    // Count unique words
    const uniqueWords = new Set(words);
    
    // Type-token ratio (TTR) - basic lexical diversity measure
    return uniqueWords.size / words.length;
  };
  
  // Count total words
  const countWords = (text) => {
    if (!text) return 0;
    return text.split(/\s+/).filter(word => word.length > 0).length;
  };
  
  // Count unique words
  const countUniqueWords = (text) => {
    if (!text) return 0;
    const words = text.toLowerCase()
      .replace(/[^\w\s]/g, '')
      .split(/\s+/)
      .filter(word => word.length > 0);
    return new Set(words).size;
  };
  
  // Get color class based on diversity category
  const getDiversityColor = (category) => {
    const colorMap = {
      'Very Low': 'bg-red-100 text-red-800',
      'Low': 'bg-orange-100 text-orange-800',
      'Medium': 'bg-yellow-100 text-yellow-800',
      'High': 'bg-green-100 text-green-800',
      'Very High': 'bg-blue-100 text-blue-800'
    };
    return colorMap[category] || 'bg-gray-100 text-gray-800';
  };
  
  // Prepare chart options for ECharts
  const getBarChartOptions = () => {
    const categories = Object.keys(distribution);
    const values = Object.values(distribution);
    
    return {
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'shadow'
        }
      },
      grid: {
        left: '3%',
        right: '4%',
        bottom: '3%',
        containLabel: true
      },
      xAxis: {
        type: 'category',
        data: categories,
        axisTick: {
          alignWithLabel: true
        }
      },
      yAxis: {
        type: 'value'
      },
      series: [
        {
          name: 'Count',
          type: 'bar',
          barWidth: '60%',
          data: values,
          itemStyle: {
            color: function(params) {
              const colors = ['#ef4444', '#f97316', '#fbbf24', '#10b981', '#3b82f6'];
              return colors[params.dataIndex];
            }
          }
        }
      ]
    };
  };
  
  // Prepare gauge chart options for average score
  const getGaugeChartOptions = () => {
    return {
      series: [
        {
          type: 'gauge',
          startAngle: 180,
          endAngle: 0,
          min: 0,
          max: 1,
          splitNumber: 5,
          axisLine: {
            lineStyle: {
              width: 30,
              color: [
                [0.3, '#f87171'],  // red
                [0.4, '#fb923c'],  // orange
                [0.5, '#fcd34d'],  // yellow
                [0.6, '#4ade80'],  // green
                [1, '#60a5fa']     // blue
              ]
            }
          },
          pointer: {
            icon: 'path://M12,9c-1.6,0-3.15,0.25-4.6,0.72v2.07c0,0.8,0.12,1.56,0.27,2.21h0.33l0.06,1.86c0.03,0.7,0.75,1.23,1.44,1.23h2.44c0.69,0,1.41-0.54,1.44-1.23l0.06-1.86h0.33c0.15-0.65,0.27-1.41,0.27-2.21v-2.07c-1.45-0.48-3-0.72-4.6-0.72z',
            length: '80%',
            width: 8,
            offsetCenter: [0, 0]
          },
          axisTick: {
            length: 12,
            lineStyle: {
              color: 'auto',
              width: 2
            }
          },
          splitLine: {
            length: 20,
            lineStyle: {
              color: 'auto',
              width: 5
            }
          },
          axisLabel: {
            color: '#464646',
            fontSize: 14,
            distance: -60,
            formatter: function (value) {
              if (value === 0.2) return 'Very Low';
              if (value === 0.4) return 'Low';
              if (value === 0.6) return 'Medium';
              if (value === 0.8) return 'High';
              if (value === 1) return 'Very High';
              return '';
            }
          },
          title: {
            offsetCenter: [0, '80%'],
            fontSize: 16
          },
          detail: {
            fontSize: 24,
            offsetCenter: [0, '40%'],
            valueAnimation: true,
            formatter: function (value) {
              return value;
            },
            color: 'inherit'
          },
          data: [
            {
              value: averageScore,
              name: 'Average Diversity'
            }
          ]
        }
      ]
    };
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-6">
      <div className="flex items-center space-x-2 mb-6">
        <SafeIcon icon={FiFileText} className="text-primary-600" />
        <h3 className="text-lg font-semibold text-gray-800">Lexical Diversity Analysis</h3>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <div>
          <h4 className="font-medium text-gray-800 mb-4">Lexical Diversity Distribution</h4>
          <div className="h-64">
            <ReactECharts 
              option={getBarChartOptions()} 
              style={{ height: '100%', width: '100%' }}
            />
          </div>
        </div>
        
        <div>
          <h4 className="font-medium text-gray-800 mb-4">Average Lexical Diversity</h4>
          <div className="h-64">
            <ReactECharts 
              option={getGaugeChartOptions()} 
              style={{ height: '100%', width: '100%' }}
            />
          </div>
        </div>
      </div>
      
      <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-gray-50 p-4 rounded-lg flex flex-col items-center justify-center text-center">
          <span className="text-gray-600 text-sm mb-1">Average Score</span>
          <span className="text-2xl font-bold text-primary-600">{averageScore}</span>
          <span className="text-xs text-gray-500 mt-1">Range: 0.0 - 1.0</span>
        </div>
        <div className="bg-gray-50 p-4 rounded-lg flex flex-col items-center justify-center text-center">
          <span className="text-gray-600 text-sm mb-1">Highest Diversity</span>
          <span className="text-2xl font-bold text-green-600">
            {diversityScores.length > 0 ? diversityScores[0].score : '0.00'}
          </span>
          <span className="text-xs text-gray-500 mt-1">More varied vocabulary</span>
        </div>
        <div className="bg-gray-50 p-4 rounded-lg flex flex-col items-center justify-center text-center">
          <span className="text-gray-600 text-sm mb-1">Lowest Diversity</span>
          <span className="text-2xl font-bold text-red-600">
            {diversityScores.length > 0 ? diversityScores[diversityScores.length - 1].score : '0.00'}
          </span>
          <span className="text-xs text-gray-500 mt-1">More repetitive vocabulary</span>
        </div>
      </div>
      
      <div className="mt-8">
        <h4 className="font-medium text-gray-800 mb-4">Sample Questions by Diversity Score</h4>
        <div className="space-y-4">
          {['Very High', 'High', 'Medium', 'Low', 'Very Low'].map(category => {
            const examples = diversityScores
              .filter(score => score.category === category)
              .slice(0, 1);
              
            if (examples.length === 0) return null;
            
            return (
              <div key={category} className="bg-gray-50 p-4 rounded-lg">
                <div className={`inline-block px-3 py-1 rounded-lg mb-3 ${getDiversityColor(category)}`}>
                  {category} ({examples[0].score})
                </div>
                {examples.map(example => (
                  <div key={example.id}>
                    <p className="text-sm text-gray-700">{example.text}</p>
                    <div className="mt-2 flex items-center space-x-4 text-xs text-gray-600">
                      <div className="flex items-center">
                        <SafeIcon icon={FiZap} className="mr-1 text-yellow-500" />
                        <span>{example.uniqueWords} unique words</span>
                      </div>
                      <div className="flex items-center">
                        <SafeIcon icon={FiFileText} className="mr-1 text-blue-500" />
                        <span>{example.totalWords} total words</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      </div>
      
      <div className="mt-8 bg-blue-50 p-4 rounded-lg">
        <h4 className="font-medium text-gray-800 mb-2">Lexical Diversity Insights</h4>
        <div className="flex items-start text-sm text-gray-600 mb-3">
          <SafeIcon icon={FiBarChart2} className="mr-2 mt-1 text-blue-500 flex-shrink-0" />
          <div>
            <p className="mb-2">Lexical diversity measures how varied the vocabulary is in your questions. Higher scores indicate more diverse language use.</p>
            <ul className="list-disc list-inside space-y-1">
              <li>Very low diversity (below 0.3) may indicate repetitive language</li>
              <li>Medium diversity (0.4-0.5) is typical for assessment questions</li>
              <li>High diversity (above 0.6) suggests sophisticated vocabulary</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LexicalDiversityAnalysis;